import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toCamelCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

export const notificationService = {
  // Get notifications for current user
  async getMyNotifications() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: [], error: null };

      const { data, error } = await supabase?.from('user_notifications')?.select(`
          *,
          project:projects(id, project_name, project_number, status)
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(50);

      if (error) throw error;
      return { data: toCamelCase(data || []), error: null };
    } catch (error) {
      console.error('Error fetching notifications:', error?.message);
      return { data: [], error };
    }
  },

  // Get unread count
  async getUnreadCount() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { count: 0, error: null };

      const { count, error } = await supabase?.from('user_notifications')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('is_read', false);

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      console.error('Error fetching unread count:', error?.message);
      return { count: 0, error };
    }
  },

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase?.from('user_notifications')?.update({ is_read: true, updated_at: new Date()?.toISOString() })?.eq('id', notificationId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error marking notification as read:', error?.message);
      return { error };
    }
  },

  // Mark all as read
  async markAllAsRead() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { error: null };

      const { error } = await supabase?.from('user_notifications')?.update({ is_read: true, updated_at: new Date()?.toISOString() })?.eq('user_id', user?.id)?.eq('is_read', false);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error marking all as read:', error?.message);
      return { error };
    }
  },

  // Create a PM assignment notification
  async createPMAssignmentNotification(pmUserId, project) {
    try {
      const { error } = await supabase?.from('user_notifications')?.insert({
          user_id: pmUserId,
          type: 'project_assignment',
          title: 'You have been assigned to a project',
          message: `You have been assigned as Project Manager for "${project?.projectName || project?.project_name}". Project #${project?.projectNumber || project?.project_number}.`,
          project_id: project?.id,
          is_read: false
        });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error creating PM notification:', error?.message);
      return { error };
    }
  },

  // Subscribe to real-time notifications
  subscribeToNotifications(userId, onNotification) {
    const channel = supabase?.channel(`notifications_${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          onNotification && onNotification(toCamelCase(payload?.new));
        }
      )?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) supabase?.removeChannel(channel);
  }
};
